import { Key, useMemo } from "react";
import { Button, TableColumnsType } from "antd";

import StatusCell from "views/uikit/VLTable/TableCells/StatusCell/StatusCell";
import { FolderMetadata, MetadataFieldKeys } from "types";
import { FolderTableData } from "./useFolderData";
import { ScanExportItem } from "./useScanExportQueue";
import styles from "./ScanImport.module.scss";

const EXPORT_KEY_PREFIX = "export:";
const PENDING_KEY_PREFIX = "pending:";

const PendingStatus = () => (
  <StatusCell
    color="warning"
    text="adding"
    loading
    tooltip={{
      title: "Adding",
      text: "Checking this folder on the server. It is listed here once that is done.",
    }}
  />
);

const ExportStatus = ({
  item,
  onRetry,
  onRemove,
}: {
  item: ScanExportItem;
  onRetry: (srPath: string) => void;
  onRemove: (srPath: string) => void;
}) => {
  if (item.status === "failed") {
    return (
      <div className={styles.exportStatus}>
        <StatusCell
          color="error"
          text="export failed"
          tooltip={{ title: "Export failed:", text: item.error ?? "" }}
        />
        <Button type="link" size="small" onClick={() => onRetry(item.srPath)}>
          Retry
        </Button>
        <Button type="link" size="small" onClick={() => onRemove(item.srPath)}>
          Remove
        </Button>
      </div>
    );
  }
  return (
    <StatusCell
      color="warning"
      // "added" lingers here only until its folder row arrives from the server.
      text={
        item.status === "queued"
          ? "queued"
          : item.status === "added"
            ? "adding"
            : "exporting"
      }
      loading={item.status !== "queued"}
      tooltip={{
        title: item.status === "queued" ? "Queued" : "Exporting",
        text: "Copying this scan result's wafers from DataServer. The folder appears here once it lands.",
      }}
    />
  );
};

interface UseExportRowsArgs {
  items: ScanExportItem[];
  /** Paths submitted from the folder dialog that the server has not listed yet. */
  pendingPaths?: string[];
  folders: FolderMetadata[];
  columns: TableColumnsType<FolderTableData>;
  dataSource: FolderTableData[];
  checkboxDisabledKeys: Key[];
  checkboxTooltipData: Record<string, string>;
  onRetry: (srPath: string) => void;
  onRemove: (srPath: string) => void;
}

/**
 * Shows work in flight as rows of the folder table - scan-result exports, and paths just
 * submitted from the folder dialog - so progress shows where the folders will appear,
 * not on a button or in a separate panel. A row stands in for
 * its folder while the export runs (or after it fails, with Retry), and gives way to the
 * real folder row once the server lists it.
 *
 * Layered over useFolderData's output rather than inside it: export rows are not folders
 * and never take part in selection or filtering.
 */
export default function useExportRows({
  items,
  pendingPaths = [],
  folders,
  columns,
  dataSource,
  checkboxDisabledKeys,
  checkboxTooltipData,
  onRetry,
  onRemove,
}: UseExportRowsArgs) {
  const shown = useMemo(
    () =>
      items.filter(
        (item) =>
          item.status !== "added" ||
          !folders.some((folder) => folder.folder_name === item.folderName),
      ),
    [items, folders],
  );

  const byKey = useMemo(
    () =>
      Object.fromEntries(
        shown.map((item) => [`${EXPORT_KEY_PREFIX}${item.srPath}`, item]),
      ),
    [shown],
  );

  const exportRows: FolderTableData[] = useMemo(
    () => [
      ...pendingPaths.map((path) => {
        const row: FolderTableData = {
          key: `${PENDING_KEY_PREFIX}${path}`,
          folderName: path,
          status: "validating",
          invalidReason: null,
          totalImages: 0,
          progress: 0,
        };
        Object.values(MetadataFieldKeys).forEach((key) => {
          row[key] = "-";
        });
        return row;
      }),
      ...shown.map((item) => {
        const row: FolderTableData = {
          key: `${EXPORT_KEY_PREFIX}${item.srPath}`,
          folderName: item.folderName ?? item.name,
          status: "validating",
          invalidReason: item.error ?? null,
          totalImages: 0,
          progress: 0,
        };
        Object.values(MetadataFieldKeys).forEach((key) => {
          row[key] = "-";
        });
        return row;
      }),
    ],
    [pendingPaths, shown],
  );

  return useMemo(() => {
    if (!exportRows.length) {
      return { columns, dataSource, checkboxDisabledKeys, checkboxTooltipData };
    }
    const exportKeys = exportRows.map((row) => row.key);
    return {
      // Newest work first: what is still moving sits above what has settled.
      dataSource: [...exportRows, ...dataSource],
      columns: columns.map((column) => {
        if (column.key === "status") {
          return {
            ...column,
            render: (value: any, record: FolderTableData, index: number) =>
              byKey[record.key] ? (
                <ExportStatus
                  item={byKey[record.key]}
                  onRetry={onRetry}
                  onRemove={onRemove}
                />
              ) : record.key.startsWith(PENDING_KEY_PREFIX) ? (
                <PendingStatus />
              ) : (
                (column as any).render?.(value, record, index)
              ),
          };
        }
        if (column.key === "totalItems") {
          return {
            ...column,
            render: (value: any, record: FolderTableData, index: number) =>
              byKey[record.key] || record.key.startsWith(PENDING_KEY_PREFIX)
                ? "-"
                : (column as any).render?.(value, record, index),
          };
        }
        return column;
      }),
      checkboxDisabledKeys: [...checkboxDisabledKeys, ...exportKeys],
      checkboxTooltipData: {
        ...checkboxTooltipData,
        ...Object.fromEntries(
          exportKeys.map((key) => [
            key,
            !byKey[key]
              ? "This folder is still being added."
              : byKey[key].status === "failed"
                ? "This scan result failed to export."
                : "This folder is still being exported.",
          ]),
        ),
      },
    };
  }, [
    byKey,
    checkboxDisabledKeys,
    checkboxTooltipData,
    columns,
    dataSource,
    exportRows,
    onRemove,
    onRetry,
  ]);
}
